import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Alert } from '@mui/material';

/**
 * FixedSnackbar - A fixed-position notification component that appears at the top of the viewport
 * This ensures notifications are always visible regardless of scroll position
 * Uses React Portal to render outside component tree, bypassing parent transforms
 * 
 * @param {boolean} open - Whether the snackbar is visible
 * @param {function} onClose - Callback when snackbar should close
 * @param {string} message - The message to display
 * @param {string} severity - Alert severity ('success', 'error', 'warning', 'info')
 * @param {number} autoHideDuration - Auto-hide duration in milliseconds (0 = no auto-hide)
 */
export default function FixedSnackbar({
  open,
  onClose,
  message,
  severity = 'success',
  autoHideDuration = 6000
}) {
  // Auto-hide effect
  useEffect(() => {
    if (!open || autoHideDuration === 0) return;

    const timer = setTimeout(() => {
      onClose();
    }, autoHideDuration);

    return () => clearTimeout(timer);
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const snackbarContent = (
    <div
      style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: 'auto',
        maxWidth: 'calc(100% - 32px)',
        animation: 'slideDown 0.3s ease-out',
        pointerEvents: 'auto'
      }}
    >
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
      <Alert
        onClose={onClose}
        severity={severity}
        sx={{
          width: '100%',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px'
        }}
      >
        {message}
      </Alert>
    </div>
  );

  // Render using Portal to bypass parent container transforms
  return createPortal(snackbarContent, document.body);
}
